import { onMounted, ref, watch } from '@vue/composition-api';
import GoogleMapsLoader from 'google-maps';

const ADDRESS_COMPONENTS = {
    subpremise: 'short_name',
    street_number: 'short_name',
    route: 'long_name',
    locality: 'long_name',
    postal_town: 'long_name',
    administrative_area_level_1: 'short_name',
    administrative_area_level_2: 'long_name',
    country: 'long_name',
    postal_code: 'short_name'
};
const ADDRESS_COMPONENTS_RANK = {
    // plus_code: 1, // indicates an encoded location reference, derived from latitude and longitude
    subpremise: 2, // unit number (apartment) // first-order entity below a named location,
    premise: 3, // named location, usually a building
    street_number: 4,
    route: 5, // street
    locality: 6, // city
    postal_town: 7, // city
    administrative_area_level_2: 8, // suburb ?
    administrative_area_level_1: 9, // province or state
    country: 10,
    postal_code: 11
};

/**
 * Use this to get a list of places based on the search key, and bias location.
 * @param {Object} options
 * @param {import('@vue/composition-api').Ref<string>} options.searchKey - The search key to search for.
 * @param {{ center: { lat: number, lng: number }, radius: number }} options.biasLocation - The location to bias the search results to. Use this to get more accurate results.
 * @param {() => void} options.onGoogleMapsLoadSuccess- Callback function to call when the component is mounted successfully.
 */

export default function useGoogleMapsAutocomplete({ searchKey, biasLocation, onGoogleMapsLoadSuccess = () => {} }) {
    const autoCompleteService = ref(null);

    /**
     *
     * @type {import("@vue/composition-api").Ref<Place[]>}
     */
    const places = ref([]);
    /**
     * State machine way of tracking status
     * |> idle -> searching -> done -> idle
     */
    const searchStatus = ref('idle');

    onMounted(async () => {
        GoogleMapsLoader.KEY = process.env.VUE_APP_GOOGLE_API_KEY;
        GoogleMapsLoader.LIBRARIES = ['geometry', 'places', 'drawing'];
        GoogleMapsLoader.VERSION = '3.53';

        await GoogleMapsLoader.load(() => {
            autoCompleteService.value = new google.maps.places.AutocompleteService();
            onGoogleMapsLoadSuccess();
        });
    });

    /**
     *
     * @param place
     * @returns {Place}
     */
    function mapPlacesResultToInternal(place) {
        return {
            placeId: place.place_id,
            description: place.description,
            matchedSubstrings: place.matched_substrings
        };
    }

    async function getPlaces() {
        if (!autoCompleteService.value) {
            return;
        }
        searchStatus.value = 'searching';
        let locationBias = null;
        if (biasLocation.value) {
            locationBias = new google.maps.Circle({
                center: {
                    lat: biasLocation.value.center.lat,
                    lng: biasLocation.value.center.lng
                },
                radius: biasLocation.value.radius
            });
        }
        const query = {
            input: searchKey.value
        };
        if (locationBias) {
            query.locationBias = locationBias;
        }

        const response = await autoCompleteService.value.getPlacePredictions(query);
        searchStatus.value = 'done';
        if (!response.predictions) {
            return;
        }
        places.value = response.predictions.map(mapPlacesResultToInternal);
    }

    function resetStatus() {
        searchStatus.value = 'idle';
    }

    function compareAddressTypeRank(currentType, incomingType) {
        if (!currentType) 
            return incomingType;
        if (!incomingType) 
            return currentType;

        if (ADDRESS_COMPONENTS_RANK[incomingType] < ADDRESS_COMPONENTS_RANK[currentType]) 
            return incomingType;
        return currentType;
    }

    function formatResult(place) {
        const returnData = {};
        for (let i = 0; i < place.address_components.length; i++) {
            const addressType = place.address_components[i].types[0];
            returnData.type = compareAddressTypeRank(returnData.type, addressType);
            if (!ADDRESS_COMPONENTS[addressType]) {
                /* eslint-disable no-continue */
                continue;
            }
            returnData[addressType] = place.address_components[i][ADDRESS_COMPONENTS[addressType]];

            if (addressType === 'country') {
                returnData.countryCode = place.address_components[i].short_name;
            } else if (addressType === 'administrative_area_level_1') {
                returnData.provinceCode = place.address_components[i].short_name;
                returnData.province = place.address_components[i].long_name;
            }
        }
        returnData.viewport = place.geometry.viewport;
        returnData.latitude = place.geometry.location.lat();
        returnData.longitude = place.geometry.location.lng();
        returnData.fullAddress = place.formatted_address;
        returnData.line1 = `${returnData.street_number || ''} ${returnData.route || ''}`.trim();
        returnData.city = returnData.locality || returnData.postal_town || returnData.administrative_area_level_3 || '';
        returnData.name = place.name;
        return returnData;
    }

    function handlePlaceResult(placeResult) {
        if (placeResult.address_components === undefined) {
            return {
                searchKey: null,
                data: null
            };
        }
        // return returnData object and PlaceResult object
        const formatted = formatResult(placeResult);
        const { latitude, longitude, fullAddress, viewport } = formatted;

        const addressComponents = {
            inputAddress: formatted.fullAddress,
            geocoder: 'Google Places - App Portal', // must be exactly the same in backend
            type: formatted.type,
            confidenceScore: null,
            address: formatted.fullAddress,
            location: {
                latitude,
                longitude
            },
            name: formatted.name,
            line1: formatted.line1,
            city: formatted.city,
            province: formatted.province,
            provinceCode: formatted.provinceCode,
            postcode: formatted.postal_code,
            country: formatted.country,
            countryCode: formatted.countryCode
        };

        const data = {
            location: {
                latitude,
                longitude
            },
            viewport,
            name: placeResult.name,
            address: fullAddress,
            addressComponents
        };
        return {
            searchKey: null,
            data
        };
    }
    async function getPlaceDetails(placeId) {
        const service = new google.maps.places.PlacesService(document.createElement('div'));
        return new Promise((resolve, reject) => {
            service.getDetails(
                {
                    placeId
                },
                (place, status) => {
                    const result = handlePlaceResult(place);
                    resolve(result);
                }
            );
        });
    }

    watch(searchKey, () => {
        if (!searchKey?.value) {
            return;
        }

        getPlaces();
    });

    return {
        places,
        searchStatus,
        getPlaceDetails,
        resetStatus
    };
}
